import { useState } from "react";
import { ArrowRight, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent, AgentPage } from "@/types/agent/agent";
import { ClosingConfig, ClosingCta, InfoLinksConfig } from "@/types/agent/info-links";

const MAX_CTAS = 4;
const MAX_MESSAGE_CHARS = 240;

interface ClosingPageCardProps {
  agent: Agent;
  brand?: string;
}

/**
 * Lets a business owner shape the end-of-conversation wrap-up: a warm closing
 * line plus a few next-step buttons. Stored nested inside `info_links.closing`
 * so it rides the existing page-config save path (no new column). Sending only
 * `{ closing }` leans on the API's deep-merge to leave links/documents intact.
 */
export const ClosingPageCard = ({ agent, brand = "#4E9CFF" }: ClosingPageCardProps) => {
  const updateAgent = useUpdateAgent();

  const getInfoLinks = (): InfoLinksConfig =>
    ((agent.page?.info_links ?? agent.info_links) as InfoLinksConfig | undefined) ?? { links: [] };
  const getClosing = (): ClosingConfig => getInfoLinks().closing ?? { message: "", ctas: [] };

  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<string>(() => getClosing().message ?? "");
  const [ctas, setCtas] = useState<ClosingCta[]>(() => getClosing().ctas ?? []);
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url, window.location.origin);
      setUrlError(null);
      return true;
    } catch {
      setUrlError("Please enter a valid URL");
      return false;
    }
  };

  const handleEdit = () => {
    const closing = getClosing();
    setMessage(closing.message ?? "");
    setCtas(closing.ctas ?? []);
    setNewLabel("");
    setNewUrl("");
    setUrlError(null);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleAddCta = () => {
    const label = newLabel.trim();
    const url = newUrl.trim();
    if (!label || !url || ctas.length >= MAX_CTAS) return;
    if (!validateUrl(url)) return;
    setCtas((prev) => [...prev, { id: `cta-${Date.now()}`, label, url }]);
    setNewLabel("");
    setNewUrl("");
    setUrlError(null);
  };

  const handleRemoveCta = (id: string) => setCtas((prev) => prev.filter((c) => c.id !== id));

  const handleSave = async () => {
    // Fold in a CTA the user typed but didn't explicitly "Add".
    const label = newLabel.trim();
    const url = newUrl.trim();
    const pending: ClosingCta[] =
      label && url && ctas.length < MAX_CTAS && validateUrl(url) ? [{ id: `cta-${Date.now()}`, label, url }] : [];

    const finalClosing: ClosingConfig = {
      message: message.trim().slice(0, MAX_MESSAGE_CHARS),
      ctas: [...ctas, ...pending],
    };

    // Send only `info_links: { closing }` — the API deep-merges, so links and
    // documents are preserved. Spread the existing page to satisfy the typed
    // shape (mirrors InfoLinksCard).
    await updateAgent.mutateAsync({
      agent,
      updateData: { page: { ...agent.page, info_links: { closing: finalClosing } } as AgentPage },
    });
    toast.success("Closing page saved");
    setCtas(finalClosing.ctas ?? []);
    setNewLabel("");
    setNewUrl("");
    setIsEditing(false);
  };

  const savedClosing = getClosing();
  const savedMessage = savedClosing.message?.trim();
  const savedCtas = savedClosing.ctas ?? [];
  const inputClass =
    "min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary";

  return (
    <OverviewSectionCard
      title="Closing page"
      subtitle="The wrap-up visitors see when a conversation ends"
      genieIcon="flag"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-5">
          {/* Closing message */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Closing message</span>
              <span className="text-xs text-gray-400">
                {message.length}/{MAX_MESSAGE_CHARS}
              </span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
              rows={2}
              placeholder="Thanks for stopping by — here's everything from our chat."
              className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
            />
            <p className="mt-1 text-xs text-gray-400">
              Leave blank to use a friendly default. Take-away documents &amp; links come from Resources.
            </p>
          </div>

          {/* Next-step CTAs */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Next-step buttons</span>
              <span className="text-xs text-gray-400">
                ({ctas.length}/{MAX_CTAS})
              </span>
            </div>

            {ctas.length > 0 && (
              <div className="mb-3 space-y-2">
                {ctas.map((cta) => (
                  <div key={cta.id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">{cta.label}</div>
                      <div className="truncate text-xs text-gray-500">{cta.url}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCta(cta.id)}
                      className="flex-shrink-0 p-1 text-gray-400 transition-colors hover:text-red-500"
                      aria-label="Remove button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {ctas.length < MAX_CTAS ? (
              <div className="space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCta();
                      }
                    }}
                    placeholder="Button label (e.g. Book a call)"
                    className={inputClass}
                  />
                  <input
                    type="url"
                    value={newUrl}
                    onChange={(e) => {
                      setNewUrl(e.target.value);
                      if (urlError) setUrlError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCta();
                      }
                    }}
                    placeholder="https://example.com/book"
                    className={`${inputClass} ${urlError ? "border-red-300" : ""}`}
                  />
                </div>
                {urlError && <p className="text-xs text-red-500">{urlError}</p>}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCta}
                  disabled={!newLabel.trim() || !newUrl.trim()}
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add button
                </Button>
              </div>
            ) : (
              <p className="text-xs text-amber-600">Maximum of {MAX_CTAS} buttons reached</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {!savedMessage && savedCtas.length === 0 ? (
            <p className="text-sm italic text-gray-400">
              Using the default wrap-up. Add a closing message and next-step buttons to make it yours.
            </p>
          ) : (
            <>
              {savedMessage && <p className="text-sm text-gray-700">{savedMessage}</p>}
              {savedCtas.map((cta) => (
                <div
                  key={cta.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900">{cta.label}</div>
                    <div className="truncate text-xs text-gray-500">{cta.url}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </OverviewSectionCard>
  );
};
